<template>
  <div
    :class="[
      $style.root,
      {
        [$style.fixedWidth]: fixedWidth,
      },
    ]"
  >
    <div
      :class="[
        $style.inputBase,
        {
          [$style.alert]: alert,
          [$style.focused]: focused,
          [$style.fixedWidth]: fixedWidth,
          [$style.fixedHeight]: fixedHeight,
          [$style.hasBefore]: slots.before,
          [$style.hasAfter]: slots.after,
          [$style.appearanceBorder]: appearance === 'border',
          [$style.appearanceTypoHeaderXlarge]: appearance === 'typo-header-xlarge',
          [$style.appearanceNoBorder]: appearance === 'no-border',
          [$style.appearanceBottomBorder]: appearance === 'bottom-border',
          [$style.inverted]: inverted,
        },
      ]"
    >
      <slot name="before" />
      <div ref="inputContainer" :class="$style.inputContainer" :underline-override="underlineOverride">
        <slot />
      </div>
      <slot name="after" />
    </div>
    <div v-if="slots['footer']" :class="$style.footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSlots } from "vue";

import type { InputAppearance } from "./types";

const slots = useSlots();

withDefaults(
  defineProps<{
    alert?: boolean;
    appearance?: InputAppearance;
    fixedWidth?: boolean;
    underlineOverride?: boolean;
    fixedHeight?: boolean;
    focused?: boolean;
    /** For a field on the brand-coloured dialog toolbar. Only `appearance="border"` styles it. */
    inverted?: boolean;
  }>(),
  {
    alert: false,
    appearance: undefined,
    fixedWidth: false,
    underlineOverride: undefined,
    fixedHeight: false,
    focused: false,
    inverted: false,
  },
);
</script>

<style module lang="scss">
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../styles/icon.scss" as IconMixins;
:root {
  --toggle-all-underlines-base: 1;
  --toggle-all-underlines-actions: 1;
}

.root {
  flex-grow: 1;
  width: 100%;
  overflow: hidden;

  &.fixedWidth {
    width: unset;
    flex: 0 0;
  }
}
.inputBase {
  @include FigmaTypoMixins.typo-body-medium;
  display: flex;
  align-items: center;
  gap: var(--space-component-padding-2xSmall) var(--sca-input-base-gap, var(--space-component-padding-small));
  flex-shrink: 0;
  width: 100%;
  padding: var(--input-base-padding);
  position: relative;
  min-height: var(--dimension-300);

  &.fixedWidth {
    width: max-content;
  }

  &.hasBefore {
    padding-left: 0;
  }
  &.hasAfter {
    padding-right: 0;
  }

  .inputContainer {
    display: flex;
    flex: 1 1;
    width: 100%;
    gap: var(--space-component-gap-small);
    align-items: center;
    justify-content: var(--sca-input-content-align);
    text-align: var(--sca-input-content-align);
    padding-top: var(--sca-input-field-wrapper-padding, var(--space-component-padding-xSmall));
    padding-bottom: var(--sca-input-field-wrapper-padding, var(--space-component-padding-xSmall));

    input,
    textarea {
      @include FigmaTypoMixins.typo-body-medium;
      flex: 1 1 100%;
      color: var(--color-content-primary);
      outline: none;
      width: 100%;
      background-color: transparent;
      text-align: var(--sca-input-content-align, start);

      &::placeholder {
        color: var(--color-content-tertiary);
      }

      &[disabled] {
        background: transparent;
      }
    }

    textarea {
      field-sizing: content;
    }

    /* Remove default browser placeholder when input is not focused */
    input[type="date"],
    input[type="month"],
    input[type="week"],
    input[type="time"],
    input[type="datetime-local"] {
      padding-bottom: calc(var(--space-component-padding-xSmall) - 3px);

      &:not(:focus):in-range {
        &::-webkit-datetime-edit-year-field,
        &::-webkit-datetime-edit-month-field,
        &::-webkit-datetime-edit-day-field,
        &::-webkit-datetime-edit-hour-field,
        &::-webkit-datetime-edit-minute-field,
        &::-webkit-datetime-edit-second-field,
        &::-webkit-datetime-edit-week-field,
        &::-webkit-datetime-edit-text {
          color: transparent;
        }
      }

      /* iOS fix for text alignment */
      &::-webkit-date-and-time-value {
        text-align: left;
        color: var(--color-content-primary);
      }

      &::-webkit-calendar-picker-indicator {
        display: none;
      }

      /* iOS background fix when input is empty */
      &:empty {
        @supports (-webkit-touch-callout: none) {
          background-color: var(--color-interaction-background-formField);
        }
      }
    }
  }

  &:has(input[readonly], textarea[readonly]) {
    input,
    textarea {
      caret-color: transparent;
      user-select: none;
    }
  }

  &:has(input[disabled], textarea[disabled]) {
    opacity: 0.5;
  }

  &:not(.appearanceBorder) {
    &:has(input:not([type="checkbox"]), textarea:not([readonly])) {
      box-shadow: inset 0px calc(-1px * var(--toggle-all-underlines-base)) 0px 0px var(--color-border-neutral-subtle);
      &:has([underline-override="true"] input:not([disabled])),
      &:not(:has(input[disabled], input[readonly], textarea[disabled], textarea[readonly])) {
        &:hover:not(.alert):not(.focused) {
          box-shadow: inset 0px calc(-1px * var(--toggle-all-underlines-actions)) 0px 0px var(--color-blue-p1);
        }

        &:has(input:focus, textarea:focus),
        &:hover:not(.alert):has(input:focus, textarea:focus),
        &:has(:global(.ql-editor):focus),
        &.focused {
          box-shadow: inset 0px calc(-2px * var(--toggle-all-underlines-actions)) 0px 0px var(--color-blue-p1);
        }

        &.alert {
          box-shadow: inset 0px calc(-1px * var(--toggle-all-underlines-actions)) 0px 0px
            var(--color-interaction-border-alert);

          &:has(input:focus, textarea:focus),
          &:hover:has(input:focus, textarea:focus) {
            box-shadow: inset 0px calc(-2px * var(--toggle-all-underlines-actions)) 0px 0px
              var(--color-interaction-border-alert);
          }
        }
      }
    }
  }
}

.appearanceBorder {
  height: 32px;
  padding: var(--space-component-padding-small) var(--space-component-padding-small);
  border-radius: var(--border-radius-large);
  background-color: var(--color-interaction-background-formField);
  box-shadow: none;

  .inputContainer {
    input,
    textarea {
      background-color: var(--color-interaction-background-formField);
      padding-bottom: var(--space-component-padding-null);
    }
  }
  &:has(input:not([type="checkbox"]), textarea) {
    box-shadow: inset 0px 0px 0px 1px var(--color-border-neutral-subtle);

    &:has([underline-override="true"] input:not([disabled])),
    &:not(:has(input[disabled], input[readonly], textarea[disabled], textarea[readonly])) {
      &:hover:not(.alert) {
        box-shadow: inset 0px 0px 0px 1px var(--color-blue-p1);
      }

      &:has(input:focus, textarea:focus),
      &:hover:not(.alert):has(input:focus, textarea:focus),
      &:has(:global(.ql-editor):focus),
      &.focused {
        box-shadow: inset 0px 0px 0px 2px var(--color-blue-p1);
      }

      &.alert {
        box-shadow: inset 0px 0px 0px 1px var(--color-interaction-border-alert);

        &:has(input:focus, textarea:focus),
        &:hover:not(.alert):has(input:focus, textarea:focus) {
          box-shadow: 0 2px 0px -0px var(--color-interaction-border-alert);
        }
      }
    }
  }
}

.appearanceBorder.inverted {
  background-color: var(--color-content-informative-primary);

  .inputContainer {
    input,
    textarea {
      background-color: transparent;
      color: var(--color-content-primary-inverted);

      &::placeholder {
        color: var(--color-content-secondary-inverted);
      }
    }
  }

  svg {
    @include IconMixins.icon-color(var(--color-content-secondary-inverted));
  }

  // Mirrors the .appearanceBorder chain one class deeper so it wins without !important.
  &:has(input:not([type="checkbox"]), textarea) {
    box-shadow: inset 0px 0px 0px 1px var(--color-nonSemantic-white-border-subtle);

    &:not(:has(input[disabled], input[readonly], textarea[disabled], textarea[readonly])) {
      &:hover:not(.alert) {
        box-shadow: inset 0px 0px 0px 1px var(--color-action-primary-subtle-selected);
      }

      &:has(input:focus, textarea:focus),
      &:hover:not(.alert):has(input:focus, textarea:focus),
      &.focused {
        background-color: transparent;
        box-shadow: inset 0px 0px 0px 1.5px var(--color-border-default);
      }
    }
  }
}

.appearanceTypoHeaderXlarge {
  min-height: 24px;
  padding: var(--space-component-padding-null);

  .inputContainer {
    padding-top: var(--space-component-padding-null);
    padding-bottom: var(--space-component-padding-xSmall);
    align-items: flex-start;

    input,
    textarea,
    div {
      @include FigmaTypoMixins.typo-header-large;
      max-height: 20px;
      padding-top: var(--space-component-padding-null);
      padding-bottom: var(--space-component-padding-null);
    }

    input,
    textarea,
    div {
      letter-spacing: 0.32px;
      height: 20px;
      line-height: 20px;
    }

    div {
      display: flex;
      align-items: center;
      height: auto;
      max-height: none;
      word-break: break-word;
    }
  }
}

.appearanceBottomBorder {
  .inputContainer {
    input,
    textarea,
    div {
      @include FigmaTypoMixins.typo-body-medium;
      max-height: 24px;
      padding-top: var(--space-component-padding-2xSmall);
      padding-bottom: var(--space-component-padding-2xSmall);
    }

    input,
    div {
      letter-spacing: 0.32px;
      height: 24px;
      line-height: 16px;
    }

    div {
      display: flex;
      align-items: center;
    }
  }
}

.appearanceBottomBorder:has(input[disabled]) {
  opacity: 1 !important;
}

.appearanceNoBorder {
  box-shadow: none !important;
}

.fixedHeight {
  height: 28px;
}
.footer {
  margin-top: -2px;
}
</style>
